import _extends from "@babel/runtime/helpers/extends";
import classnames from "classnames/dedupe";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { FileItem } from "./components";

const readUploadedFileAsBinary = inputFile => {
  const temporaryFileReader = new FileReader();
  return new Promise((resolve, reject) => {
    temporaryFileReader.onerror = () => {
      temporaryFileReader.abort();
      reject(new DOMException("Problem parsing input file."));
    };

    temporaryFileReader.onload = () => {
      resolve(temporaryFileReader.result);
    };

    temporaryFileReader.readAsArrayBuffer(inputFile);
  });
};

const filterFilesBySignature = async (files, signature) => {
  const pdfFiles = await Promise.all(files.map(async file => {
    const bytes = await readUploadedFileAsBinary(file);
    const view = new Uint8Array(bytes);
    const first4Characters = String.fromCharCode(view[0], view[1], view[2], view[3]);
    return signature === first4Characters ? file : false;
  }));
  return pdfFiles.filter(file => !!file);
};

const getFileDropped = (files, filteredFiles) => files.find((file, idx) => file !== filteredFiles[idx]);

export const FileUpload = /*#__PURE__*/React.forwardRef(
/**
 *
 * @param {object} props
 * @param {string} [props.signature] first 4 characters of the file
 * @param {string} props.accept defines the file types e.g. ".pdf" or "application/msword"
 * @param {(files: File[]) => Promise<any>} props.onChange
 * @param {any} props.children
 * @param {any} props.className
 * @param {boolean} [props.noFilesDisplay]
 * @returns {JSX.Element}
 */
function FileUpload({
  signature,
  accept,
  children,
  className,
  noFilesDisplay,
  onChange,
  ...props
}, refIn) {
  /** @type {[File[], (files: File[]) => void]} */
  const [files, setFiles] = useState([]);
  const newRef = useRef(null);
  const ref = refIn ?? newRef;
  const [dragDropErrorMsg, setDragDropErrorMsg] = useState(null);
  const matchExtension = accept?.split(",").find(pattern => pattern.startsWith("."));

  const resetInputValue = () => {
    if (ref) {
      ref["current"].value = null;
    }
  };

  const removeFile = file => setFiles(files.filter(f => f !== file));

  const labelClassName = classnames("form-button relative", className);
  useEffect(() => {
    onChange(files);
  }, [files]);
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("label", {
    className: labelClassName
  }, /*#__PURE__*/React.createElement("input", _extends({}, props, {
    className: "opacity-0 w-full h-full cursor-pointer absolute",
    type: "file",
    ref: ref,
    accept: accept,
    onClick: resetInputValue,
    onChange: async event => {
      const files = Array.from(event.currentTarget.files);
      let filteredFiles = files;
      let cause = "";

      if (matchExtension) {
        filteredFiles = files.filter(file => file.name.toLowerCase().endsWith(matchExtension));

        if (files.length !== filteredFiles.length) {
          cause = "file extension";
        }
      }

      if (signature && !cause) {
        filteredFiles = await filterFilesBySignature(files, signature);

        if (files.length !== filteredFiles.length) {
          cause = "signature";
        }
      }

      const droppedFile = getFileDropped(files, filteredFiles);

      if (droppedFile) {
        resetInputValue();
        setDragDropErrorMsg( /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("span", {
          className: "font-bold"
        }, " ", droppedFile.name, " "), "doesn't match", /*#__PURE__*/React.createElement("span", {
          className: "font-bold"
        }, " ", cause, " "), "so it was not accepted"));
        setTimeout(() => {
          setDragDropErrorMsg(null);
        }, 4000);
      }

      setFiles(filteredFiles);
    }
  })), children)), dragDropErrorMsg && /*#__PURE__*/React.createElement("div", {
    className: "pl-4 text-sm truncate text-en-red"
  }, dragDropErrorMsg), files?.length > 0 && !noFilesDisplay ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "overflow-x-auto"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex flex-wrap justify-center md:justify-start"
  }, files.map(file => /*#__PURE__*/React.createElement(FileItem, {
    removeFile: () => {
      resetInputValue();
      removeFile(file);
    },
    name: file.name,
    key: file.name
  }))))) : null);
});
FileUpload.propTypes = {
  signature: PropTypes.string,
  accept: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.any,
  onChange: PropTypes.func,
  noFilesDisplay: PropTypes.bool
};
FileUpload.defaultProps = {
  onChange: () => Promise.resolve()
};